/**
 * Parser/serializer voor de product-/verkoopomschrijving (Unit4 taalnotitie N/E) volgens het
 * Klinger-sjabloon: afsluiter → "Voorzien van / Mounted with:" → koppel-/brugstuk → actuator →
 * magneetventiel → switchbox/sensor/positioner → filter/reduceer → fitwerk → (optioneel) sizing.
 *
 * De tekst bestaat uit alinea's (gescheiden door een lege regel). De parser classificeert elke
 * alinea op trefwoorden en is verliesvrij: wat niet herkend wordt gaat naar `vrijeTekst` en komt
 * bij serialiseren ongewijzigd achteraan terug. Labels zijn per taal (NL/EN); de parser herkent
 * beide talen ongeacht de gekozen taal.
 */
import type { DescriptionLang, SalesDescriptionSuggestion } from './salesDescription.js';

export interface SalesDescriptionModel {
  /** Afsluiter: merk, type, maat/aansluiting, materialen — meerregelig */
  afsluiter: string;
  /** Koppel-/brugstuk, asadapter, montageplaatje */
  koppelstuk: string;
  actuator: {
    type: string;
    /** stuurdruk in bar (alleen het getal) */
    stuurdruk: string;
    montage: string;
    werking: string;
    eindkappen: string;
  };
  magneetventiel: string;
  switchbox: string;
  filterReduceer: string;
  fitwerk: string;
  sizing: {
    aan: boolean;
    airSupply: string;
    maxDp: string;
    safetyFactor: string;
    torqueEx: string;
    torqueIncl: string;
    /** druk (barg) waarbij het actuatorkoppel is opgegeven */
    actuatorTorquePressure: string;
    /** bv. "99/65 x 94/60" */
    actuatorTorque: string;
    mast: string;
    sluittijd: string;
    openingstijd: string;
  };
  /** Niet-herkende alinea's — blijven verbatim behouden (achteraan) */
  vrijeTekst: string;
}

export interface ParseResult {
  model: SalesDescriptionModel;
  warnings: string[];
}

export const WERKING_OPTIES = [
  'Air Failure Close (AFC)',
  'Air Failure Open (AFO)',
  'Stay In Place (STIP)',
  'Double Acting',
];
export const MONTAGE_OPTIES: Record<DescriptionLang, string[]> = {
  en: ['In line', 'Perpendicular to the pipe'],
  nl: ['Evenwijdig aan de leiding', 'Haaks op de leiding'],
};

interface Labels {
  mounted: string;
  airSupply: string;
  mounting: string;
  action: string;
  endCaps: string;
  sizing: string;
  maxDp: string;
  safety: string;
  torqueEx: string;
  torqueIncl: string;
  actTorque: (pressure: string) => string;
  mast: string;
  times: (close: string, open: string) => string;
}

const LABELS: Record<DescriptionLang, Labels> = {
  en: {
    mounted: 'Mounted with:',
    airSupply: 'Actuator air supply',
    mounting: 'Actuator mounted',
    action: 'Actuator action',
    endCaps: 'End caps of actuator',
    sizing: 'Sizing:',
    maxDp: 'Max Act. sizing dP.',
    safety: 'Safety factor',
    torqueEx: 'Valve torque ex safety factor',
    torqueIncl: 'Valve torque including safety factor',
    actTorque: (p) => `Actuator torque @ ${p || '...'} barg air/spring`,
    mast: 'MAST',
    times: (c, o) => `Closing time ${c || '...'} / Opening time ${o || '...'} seconds`,
  },
  nl: {
    mounted: 'Voorzien van:',
    airSupply: 'Actuator stuurdruk',
    mounting: 'Actuator montage',
    action: 'Actuator werking bij pneumatisch falen',
    endCaps: 'Eindkappen van actuator',
    sizing: 'Sizing:',
    maxDp: 'Max Act. sizing dP.',
    safety: 'Safety factor',
    torqueEx: 'Valve torque ex safety factor',
    torqueIncl: 'Valve torque incl. safety factor',
    actTorque: (p) => `Actuator torque @ ${p || '...'} barg lucht/veer`,
    mast: 'MAST',
    times: (c, o) => `Sluittijd ${c || '...'} / Openingstijd ${o || '...'} seconden`,
  },
};

export function emptyModel(): SalesDescriptionModel {
  return {
    afsluiter: '',
    koppelstuk: '',
    actuator: { type: '', stuurdruk: '', montage: '', werking: '', eindkappen: '' },
    magneetventiel: '',
    switchbox: '',
    filterReduceer: '',
    fitwerk: '',
    sizing: {
      aan: false,
      airSupply: '',
      maxDp: '',
      safetyFactor: '',
      torqueEx: '',
      torqueIncl: '',
      actuatorTorquePressure: '',
      actuatorTorque: '',
      mast: '',
      sluittijd: '',
      openingstijd: '',
    },
    vrijeTekst: '',
  };
}

/** true als het model geen enkele ingevulde sectie heeft */
export function isEmptyModel(m: SalesDescriptionModel): boolean {
  const a = m.actuator;
  return (
    !m.afsluiter.trim() &&
    !m.koppelstuk.trim() &&
    !(a.type || a.stuurdruk || a.montage || a.werking || a.eindkappen).trim() &&
    !m.magneetventiel.trim() &&
    !m.switchbox.trim() &&
    !m.filterReduceer.trim() &&
    !m.fitwerk.trim() &&
    !m.sizing.aan &&
    !m.vrijeTekst.trim()
  );
}

// ---------- hulpfuncties ----------

const clean = (s: string | null | undefined): string => (s ?? '').replace(/\r/g, '').trim();

/** '...' of leeg = nog niet ingevuld */
function isPlaceholder(v: string): boolean {
  const t = v.trim();
  return !t || /^\.{2,}$/.test(t) || /^…$/.test(t);
}

/** Waarde zonder eenheid (bar/barg/Nm/s/seconden) en zonder placeholder */
function stripUnit(v: string): string {
  const t = v
    .trim()
    .replace(/\s*(barg?|nm|seconds?|seconden|sec\.?|s)\.?$/i, '')
    .trim();
  return isPlaceholder(t) ? '' : t;
}

/** Herkent een keuzewaarde (ongeacht hoofdletters/afkorting); onbekende waarden blijven staan. */
function matchOptie(value: string, opties: string[]): string {
  const v = value.trim().replace(/\s+/g, ' ');
  if (isPlaceholder(v)) return '';
  const u = v.toUpperCase();
  const hit = opties.find((o) => {
    const ou = o.toUpperCase();
    if (ou === u) return true;
    // afkorting tussen haakjes (AFC/AFO/STIP) of het deel vóór de haakjes
    const abbr = ou.match(/\(([^)]+)\)/)?.[1];
    const base = ou.replace(/\s*\(.*\)\s*$/, '');
    return (abbr && (u === abbr || u === `(${abbr})`)) || u === base;
  });
  return hit ?? v;
}

/** Volledige optielijst uit het sjabloon ("A / B / C") = geen keuze gemaakt */
function isOptionList(value: string, opties: string[]): boolean {
  const parts = value
    .split('/')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  if (parts.length < 2) return false;
  return parts.every((p) =>
    opties.some((o) => {
      const ou = o.toUpperCase();
      return ou.startsWith(p) || p.startsWith(ou.replace(/\s*\(.*\)\s*$/, '')) || ou.includes(p);
    }),
  );
}

function isMountedMarker(line: string): boolean {
  const t = line
    .trim()
    .replace(/[:\s]+$/, '')
    .toUpperCase();
  return t === 'MOUNTED WITH' || t === 'VOORZIEN VAN' || t === 'VOORZIEN VAN / MOUNTED WITH';
}

type Kind =
  | 'afsluiter'
  | 'koppelstuk'
  | 'actuator'
  | 'magneetventiel'
  | 'switchbox'
  | 'filterReduceer'
  | 'fitwerk'
  | 'sizing'
  | 'vrij';

const KIND_LABEL: Record<Exclude<Kind, 'vrij'>, string> = {
  afsluiter: 'Afsluiter',
  koppelstuk: 'Koppel-/brugstuk',
  actuator: 'Actuator',
  magneetventiel: 'Magneetventiel',
  switchbox: 'Switchbox/sensor/positioner',
  filterReduceer: 'Filter/reduceer',
  fitwerk: 'Fitwerk',
  sizing: 'Sizing',
};

function classify(para: string, afterMounted: boolean, claimed: Set<Kind>): Kind {
  const first = para.split('\n')[0].trim();
  if (/^sizing\b/i.test(first)) return 'sizing';
  if (
    /\b(actuator|aandrijving)\b/i.test(para) &&
    /\b(type|pneumat|electr|elektr|air supply|stuurdruk|mounted|montage|action|werking|end caps|eindkappen|double acting|spring return|veer)/i.test(
      para,
    )
  )
    return 'actuator';
  // vóór "Mounted with" is de eerste onbekende alinea de afsluiter zelf
  if (!afterMounted && !claimed.has('afsluiter')) return 'afsluiter';
  // switchbox vóór magneetventiel: "NAMUR" komt in beide voor (NAMUR-sensoren resp. NAMUR-aansluiting)
  if (/switch ?box|sensor|positioner|limit switch|eindschakelaar|proximity/i.test(para)) return 'switchbox';
  if (/magneetventiel|solenoid|namur|snelheidsregel|speed control|verloopplaat|quick exhaust/i.test(para))
    return 'magneetventiel';
  if (/\bfilter\b/i.test(para) && /reduceer|regulator|reducer|lubricator/i.test(para))
    return 'filterReduceer';
  if (/^filter/i.test(first)) return 'filterReduceer';
  if (/fitwerk|fitting|tubing|starre leiding|piping|leidingwerk/i.test(para)) return 'fitwerk';
  if (/coupling|bracket|koppel|brugstuk|montageplaat|adapter|asvierkant|mounting kit/i.test(para))
    return 'koppelstuk';
  return 'vrij';
}

function parseActuator(para: string, m: SalesDescriptionModel, warnings: string[]): void {
  const typeLines: string[] = [];
  for (const raw of para.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    let mm: RegExpMatchArray | null;
    if (
      (mm = line.match(
        /^(?:actuator\s+)?(?:air supply|stuurdruk|supply pressure|control pressure)\s*:\s*(.*)$/i,
      ))
    )
      m.actuator.stuurdruk = stripUnit(mm[1]);
    else if (
      (mm = line.match(
        /^(?:actuator\s+)?(?:mounted|mounting|montage|orientation|ori[eë]ntatie)\s*:\s*(.*)$/i,
      ))
    ) {
      const v = mm[1].trim();
      m.actuator.montage =
        isOptionList(v, [...MONTAGE_OPTIES.en, ...MONTAGE_OPTIES.nl]) || isPlaceholder(v)
          ? ''
          : matchOptie(v, [...MONTAGE_OPTIES.en, ...MONTAGE_OPTIES.nl]);
    } else if ((mm = line.match(/^(?:actuator\s+)?(?:action|werking)[^:]*:\s*(.*)$/i))) {
      const v = mm[1].trim();
      m.actuator.werking =
        isOptionList(v, WERKING_OPTIES) || isPlaceholder(v) ? '' : matchOptie(v, WERKING_OPTIES);
    } else if ((mm = line.match(/^(?:end caps|endcaps|eindkappen)[^:]*:\s*(.*)$/i))) {
      const v = mm[1].trim();
      m.actuator.eindkappen = isPlaceholder(v) ? '' : v;
    } else typeLines.push(line);
  }
  m.actuator.type = typeLines.join('\n');
  if (!m.actuator.type) warnings.push('Actuator: type/omschrijving ontbreekt');
}

function parseSizing(para: string, m: SalesDescriptionModel, warnings: string[]): string[] {
  const rest: string[] = [];
  const s = m.sizing;
  s.aan = true;
  for (const raw of para.split('\n').slice(1)) {
    const line = raw.trim();
    if (!line) continue;
    let mm: RegExpMatchArray | null;
    if ((mm = line.match(/^(?:actuator\s+)?(?:air supply|stuurdruk)\s*:\s*(.*)$/i)))
      s.airSupply = stripUnit(mm[1]);
    else if ((mm = line.match(/max\.?\s*act\.?\s*sizing\s*dp\.?\s*:\s*(.*)$/i))) s.maxDp = stripUnit(mm[1]);
    else if ((mm = line.match(/^safety factor\s*:\s*(.*)$/i))) s.safetyFactor = stripUnit(mm[1]);
    else if ((mm = line.match(/torque\s+ex(?:cl\.?|cluding)?\.?\s+safety factor\s*:\s*(.*)$/i)))
      s.torqueEx = stripUnit(mm[1]);
    else if ((mm = line.match(/torque\s+(?:incl\.?|including)\s+safety factor\s*:\s*(.*)$/i)))
      s.torqueIncl = stripUnit(mm[1]);
    else if ((mm = line.match(/^actuator\s+(?:torque|force)\s*@\s*([^\s:]*)\s*barg?[^:]*:\s*(.*)$/i))) {
      s.actuatorTorquePressure = isPlaceholder(mm[1]) ? '' : mm[1].trim();
      s.actuatorTorque = stripUnit(mm[2]);
    } else if ((mm = line.match(/^mast\s*:\s*(.*)$/i))) s.mast = stripUnit(mm[1]);
    else if (/closing time|sluittijd|opening time|openingstijd/i.test(line)) {
      const c = line.match(/(?:closing time|sluittijd)\s*:?\s*([\d.,]+)/i)?.[1] ?? '';
      const o = line.match(/(?:opening time|openingstijd)\s*:?\s*([\d.,]+)/i)?.[1] ?? '';
      s.sluittijd = c;
      s.openingstijd = o;
    } else rest.push(line);
  }
  if (rest.length) warnings.push(`Sizing: ${rest.length} regel(s) niet herkend — behouden als overige tekst`);
  return rest;
}

// ---------- parse ----------

export function parseSalesDescription(text: string | null | undefined): ParseResult {
  const model = emptyModel();
  const warnings: string[] = [];
  const t = clean(text);
  if (!t) return { model, warnings };

  const paragraphs = t
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+$/, '').replace(/^\n+/, ''))
    .filter((p) => p.trim());
  const claimed = new Set<Kind>();
  const vrij: string[] = [];
  let afterMounted = false;

  for (let para of paragraphs) {
    const lines = para.split('\n');
    if (isMountedMarker(lines[0])) {
      afterMounted = true;
      lines.shift();
      if (!lines.join('').trim()) continue;
      para = lines.join('\n');
    }
    const kind = classify(para, afterMounted, claimed);
    if (kind === 'vrij') {
      vrij.push(para);
      continue;
    }
    if (claimed.has(kind)) {
      vrij.push(para);
      warnings.push(`Tweede alinea voor "${KIND_LABEL[kind]}" — behouden als overige tekst`);
      continue;
    }
    claimed.add(kind);
    if (kind !== 'afsluiter') afterMounted = true;
    switch (kind) {
      case 'afsluiter':
        model.afsluiter = para;
        break;
      case 'koppelstuk':
        model.koppelstuk = para;
        break;
      case 'actuator':
        parseActuator(para, model, warnings);
        break;
      case 'magneetventiel':
        model.magneetventiel = para;
        break;
      case 'switchbox':
        model.switchbox = para;
        break;
      case 'filterReduceer':
        model.filterReduceer = para;
        break;
      case 'fitwerk':
        model.fitwerk = para;
        break;
      case 'sizing':
        vrij.push(...parseSizing(para, model, warnings));
        break;
    }
  }
  model.vrijeTekst = vrij.join('\n\n');
  if (vrij.length && !warnings.some((w) => w.startsWith('Sizing')))
    warnings.push(
      `${vrij.length} alinea('s) niet herkend als sjabloonsectie — behouden onder "Overige tekst"`,
    );
  return { model, warnings };
}

// ---------- serialize ----------

function actuatorLines(a: SalesDescriptionModel['actuator'], l: Labels): string[] {
  const out: string[] = [];
  if (clean(a.type)) out.push(clean(a.type));
  if (clean(a.stuurdruk)) out.push(`${l.airSupply} : ${clean(a.stuurdruk)} bar`);
  if (clean(a.montage)) out.push(`${l.mounting} : ${clean(a.montage)}`);
  if (clean(a.werking)) out.push(`${l.action} : ${clean(a.werking)}`);
  if (clean(a.eindkappen)) out.push(`${l.endCaps} : ${clean(a.eindkappen)}`);
  return out;
}

function sizingLines(s: SalesDescriptionModel['sizing'], l: Labels): string[] {
  const v = (x: string) => clean(x) || '...';
  return [
    l.sizing,
    `${l.airSupply}: ${v(s.airSupply)} bar`,
    `${l.maxDp}: ${v(s.maxDp)} bar`,
    `${l.safety} : ${v(s.safetyFactor)}`,
    `${l.torqueEx} : ${v(s.torqueEx)} Nm`,
    `${l.torqueIncl} : ${v(s.torqueIncl)} Nm`,
    `${l.actTorque(clean(s.actuatorTorquePressure))}: ${v(s.actuatorTorque)} Nm`,
    `${l.mast} : ${v(s.mast)} Nm`,
    l.times(clean(s.sluittijd), clean(s.openingstijd)),
  ];
}

export function serializeSalesDescription(m: SalesDescriptionModel, lang: DescriptionLang): string {
  const l = LABELS[lang];
  const blocks: string[] = [];
  if (clean(m.afsluiter)) blocks.push(clean(m.afsluiter));
  const mounted = [
    clean(m.koppelstuk),
    actuatorLines(m.actuator, l).join('\n'),
    clean(m.magneetventiel),
    clean(m.switchbox),
    clean(m.filterReduceer),
    clean(m.fitwerk),
  ].filter(Boolean);
  if (mounted.length) blocks.push(`${l.mounted}\n${mounted[0]}`, ...mounted.slice(1));
  if (m.sizing.aan) blocks.push(sizingLines(m.sizing, l).join('\n'));
  if (clean(m.vrijeTekst)) blocks.push(clean(m.vrijeTekst));
  return blocks.join('\n\n');
}

// ---------- AI-voorstel → model ----------

/**
 * Past een AI-voorstel toe op het model: alleen gevulde velden overschrijven, de rest blijft staan.
 * Geeft de namen van de secties terug die gewijzigd zijn (voor de melding).
 */
export function applySuggestion(
  m: SalesDescriptionModel,
  s: SalesDescriptionSuggestion,
): { model: SalesDescriptionModel; changed: string[] } {
  const model: SalesDescriptionModel = JSON.parse(JSON.stringify(m));
  const changed: string[] = [];
  const take = (v: string | null | undefined): string | null => {
    const c = clean(v);
    return c && !isPlaceholder(c) ? c : null;
  };
  const setText = (
    key: 'afsluiter' | 'koppelstuk' | 'magneetventiel' | 'switchbox' | 'filterReduceer' | 'fitwerk',
    v: string | null | undefined,
    label: string,
  ) => {
    const t = take(v);
    if (t !== null && t !== model[key]) {
      model[key] = t;
      changed.push(label);
    }
  };
  setText('afsluiter', s.valve, 'Afsluiter');
  setText('koppelstuk', s.coupling, 'Koppel-/brugstuk');
  const act: Array<[keyof SalesDescriptionModel['actuator'], string | null | undefined]> = [
    ['type', s.actuator_type],
    ['stuurdruk', s.actuator_air_supply],
    ['montage', s.actuator_mounting],
    ['werking', s.actuator_action],
    ['eindkappen', s.actuator_end_caps],
  ];
  let actChanged = false;
  for (const [k, v] of act) {
    const t = take(v);
    if (t === null) continue;
    const val =
      k === 'werking'
        ? matchOptie(t, WERKING_OPTIES)
        : k === 'montage'
          ? matchOptie(t, [...MONTAGE_OPTIES.en, ...MONTAGE_OPTIES.nl])
          : k === 'stuurdruk'
            ? stripUnit(t)
            : t;
    if (val && val !== model.actuator[k]) {
      model.actuator[k] = val;
      actChanged = true;
    }
  }
  if (actChanged) changed.push('Actuator');
  setText('magneetventiel', s.solenoid, 'Magneetventiel');
  setText('switchbox', s.switchbox, 'Switchbox/sensor/positioner');
  setText('filterReduceer', s.filter_regulator, 'Filter/reduceer');
  setText('fitwerk', s.fittings, 'Fitwerk');
  if (s.sizing) {
    const z = s.sizing;
    const pairs: Array<[keyof Omit<SalesDescriptionModel['sizing'], 'aan'>, string | null | undefined]> = [
      ['airSupply', z.air_supply],
      ['maxDp', z.max_dp],
      ['safetyFactor', z.safety_factor],
      ['torqueEx', z.torque_ex],
      ['torqueIncl', z.torque_incl],
      ['actuatorTorquePressure', z.actuator_torque_pressure],
      ['actuatorTorque', z.actuator_torque],
      ['mast', z.mast],
      ['sluittijd', z.closing_time],
      ['openingstijd', z.opening_time],
    ];
    let any = false;
    for (const [k, v] of pairs) {
      const t = take(v);
      if (t === null) continue;
      const val = stripUnit(t);
      if (val && val !== model.sizing[k]) {
        model.sizing[k] = val;
        any = true;
      }
    }
    if (any) {
      model.sizing.aan = true;
      changed.push('Sizing');
    }
  }
  return { model, changed };
}
